import React, { useState, useEffect } from 'react';
import OrderSelectorModal from './OrderSelectorModal';
import ProductSelectorModal from './ProductSelectorModal';
import { formatCurrency } from '../utils/formatters';
import './QuotationFormModal.css';

const QuotationFormModal = ({ onClose, onSuccess, supplier = null, quotationId = null }) => {
    // Form States
    const [cotacaoDate, setCotacaoDate] = useState(new Date().toISOString().split('T')[0]);
    const [cotacaoDuration, setCotacaoDuration] = useState('30');
    const [cotacaoDesc, setCotacaoDesc] = useState('');
    const [userObs, setUserObs] = useState('');
    const [linkedOrder, setLinkedOrder] = useState(null);
    const [cotacaoItems, setCotacaoItems] = useState([{ NomeProduto: '', Quantidade: 1, IDProduto: null }]);

    // UI States
    const [isOrderSelectorOpen, setIsOrderSelectorOpen] = useState(false);
    const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
    const [activeItemIndex, setActiveItemIndex] = useState(null);
    const [successData, setSuccessData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Load data if editing
    useEffect(() => {
        if (quotationId) {
            setLoading(true);
            fetch(`/api/cotacoes/${quotationId}`)
                .then(res => res.json())
                .then(data => {
                    setCotacaoDate(data.DataCotacao ? data.DataCotacao.split('T')[0] : '');
                    setCotacaoDuration(data.DuracaoDias?.toString() || '30');
                    setCotacaoDesc(data.Descricao || '');
                    setUserObs(data.ObservacaoUsuario || '');
                    setCotacaoItems(data.itens || []);
                    if (data.idPedidoOrigem) {
                        setLinkedOrder({ ID: data.idPedidoOrigem });
                    }
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Erro ao carregar cotação:', err);
                    setLoading(false);
                });
        }
    }, [quotationId]);

    const addCotacaoItem = () => {
        setCotacaoItems([...cotacaoItems, { NomeProduto: '', Quantidade: 1, IDProduto: null }]);
    };

    const removeCotacaoItem = (idx) => {
        setCotacaoItems(cotacaoItems.filter((_, i) => i !== idx));
    };

    const updateCotacaoItem = (idx, field, value) => {
        const newItems = [...cotacaoItems];
        newItems[idx][field] = value;
        setCotacaoItems(newItems);
    };

    const openProductSearch = (idx) => {
        setActiveItemIndex(idx);
        setIsProductSelectorOpen(true);
    };

    const handleProductSelect = (product) => {
        updateCotacaoItem(activeItemIndex, 'NomeProduto', product.NomeProduto);
        updateCotacaoItem(activeItemIndex, 'IDProduto', product.ID);
        setIsProductSelectorOpen(false);
    };

    const handleSave = async (status) => {
        if (cotacaoItems.length === 0) return alert('Adicione pelo menos um item.');

        const payload = {
            IDFornecedor: supplier?.ID || null, // Will be ignored on update by backend logic or used if provided
            idPedidoOrigem: linkedOrder?.ID || null,
            DuracaoDias: cotacaoDuration,
            DataCotacao: cotacaoDate,
            ObservacaoUsuario: userObs,
            Descricao: cotacaoDesc,
            itens: cotacaoItems,
            status: status
        };

        setLoading(true);
        try {
            const url = quotationId ? `/api/cotacoes/${quotationId}` : '/api/cotacoes';
            const method = quotationId ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include'
            });

            if (res.ok) {
                const data = await res.json();
                if (status === 'Pendente' && !quotationId) {
                    setSuccessData({
                        ...data,
                        token: data.token,
                        senha: data.senha
                    });
                } else {
                    onSuccess();
                }
            } else {
                const err = await res.json();
                alert(`Erro: ${err.message || 'Erro ao salvar cotação'}`);
            }
        } catch (error) {
            console.error('Erro ao salvar cotação:', error);
            alert('Erro de conexão ao salvar cotação.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copiado!');
    };

    if (successData) {
        const publicLink = `${window.location.origin}/public-quotation/${successData.token}`;
        return (
            <div className="modal-overlay">
                <div className="modal-content success-window">
                    <div className="win-title">Cotação Gerada com Sucesso!</div>
                    <div className="win-body">
                        <p>A solicitação foi criada e o link de acesso está disponível abaixo:</p>

                        <div className="final-info-box">
                            <label>Link de Acesso:</label>
                            <div className="input-copy-group">
                                <input type="text" readOnly value={publicLink} />
                                <button className="btn-classic" onClick={() => copyToClipboard(publicLink)}>Copiar</button>
                            </div>

                            <label style={{ marginTop: '10px', display: 'block' }}>Senha de Acesso:</label>
                            <div className="input-copy-group">
                                <input type="text" readOnly value={successData.senha} />
                                <button className="btn-classic" onClick={() => copyToClipboard(successData.senha)}>Copiar</button>
                            </div>
                        </div>

                        <div className="summary-section">
                            <h4>Resumo do Envio</h4>
                            <p><strong>Fornecedor:</strong> {supplier?.NomeEmpresa}</p>
                            <p><strong>Itens:</strong> {cotacaoItems.length}</p>
                        </div>
                    </div>
                    <div className="modal-footer" style={{ borderTop: 'none' }}>
                        <button className="btn-classic primary" onClick={onSuccess}>Concluir</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay">
            <div className="modal-content cotacao-modal">
                <div className="win-title">
                    {quotationId ? `Editar Cotação #${quotationId}` : `Nova Solicitação - ${supplier?.NomeEmpresa}`}
                    <button className="btn-close" onClick={onClose}>×</button>
                </div>
                <div className="win-body">
                    {loading && !quotationId ? <p>Carregando...</p> : (
                        <div className="form-container">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Data da Cotação</label>
                                    <input type="date" value={cotacaoDate} onChange={(e) => setCotacaoDate(e.target.value)} required />
                                </div>
                                <div className="form-group">
                                    <label>Validade (Dias)</label>
                                    <select value={cotacaoDuration} onChange={(e) => setCotacaoDuration(e.target.value)}>
                                        <option value="30">30 dias</option>
                                        <option value="60">60 dias</option>
                                        <option value="90">90 dias</option>
                                        <option value="180">180 dias</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Descrição / Nome da Cotação (Opcional)</label>
                                <input
                                    type="text"
                                    placeholder="Ex: Reforma Banheiro, Material Elétrico..."
                                    value={cotacaoDesc}
                                    onChange={(e) => setCotacaoDesc(e.target.value)}
                                />
                            </div>

                            <div className="form-group">
                                <label>Vincular a Orçamento</label>
                                <div style={{ display: 'flex', gap: '5px' }}>
                                    <input
                                        type="text"
                                        readOnly
                                        placeholder="Nenhum orçamento vinculado"
                                        value={linkedOrder ? `Orçamento #${linkedOrder.ID}` : ''}
                                    />
                                    <button type="button" className="btn-classic" onClick={() => setIsOrderSelectorOpen(true)}>
                                        {linkedOrder ? 'Alterar' : 'Vincular'}
                                    </button>
                                    {linkedOrder && <button type="button" className="btn-classic danger" onClick={() => setLinkedOrder(null)}>×</button>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Observações para o Fornecedor</label>
                                <textarea
                                    value={userObs}
                                    onChange={(e) => setUserObs(e.target.value)}
                                    placeholder="Ex: Por favor, enviar preços com impostos inclusos."
                                />
                            </div>

                            <div className="cotacao-items-section">
                                <h4>Produtos</h4>
                                {cotacaoItems.map((item, idx) => (
                                    <div key={idx} className="cotacao-item-row">
                                        <div className="input-with-search">
                                            <input
                                                type="text"
                                                placeholder="Nome do Produto"
                                                value={item.NomeProduto}
                                                onChange={(e) => updateCotacaoItem(idx, 'NomeProduto', e.target.value)}
                                                required
                                            />
                                            <button type="button" className="btn-search-prod" onClick={() => openProductSearch(idx)} title="Buscar nos Produtos">📦</button>
                                        </div>
                                        <input
                                            type="number"
                                            placeholder="Qtd"
                                            className="qtd-input"
                                            value={item.Quantidade}
                                            onChange={(e) => updateCotacaoItem(idx, 'Quantidade', e.target.value)}
                                            required
                                        />
                                        {item.PrecoOfertado && (
                                            <div className="price-display-wrapper">
                                                <span>{formatCurrency(item.PrecoOfertado)}</span>
                                            </div>
                                        )}
                                        <button type="button" className="btn-remove-item" onClick={() => removeCotacaoItem(idx)}>×</button>
                                    </div>
                                ))}
                                <button type="button" className="btn-add-item" onClick={addCotacaoItem}>+ Adicionar Item</button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <div className="modal-actions">
                        <button className="btn-classic primary" onClick={() => handleSave('Pendente')} disabled={loading}>
                            Salvar e Publicar
                        </button>
                        <button className="btn-classic" onClick={() => handleSave('Rascunho')} disabled={loading}>
                            Salvar Rascunho
                        </button>
                        <button className="btn-classic danger" onClick={onClose} disabled={loading}>Cancelar</button>
                    </div>
                </div>
            </div>

            {isOrderSelectorOpen && (
                <OrderSelectorModal
                    title="Selecionar Cliente e Orçamento"
                    onClose={() => setIsOrderSelectorOpen(false)}
                    onSelect={(order) => {
                        setLinkedOrder(order);
                        setIsOrderSelectorOpen(false);
                    }}
                />
            )}

            {isProductSelectorOpen && (
                <ProductSelectorModal
                    onClose={() => setIsProductSelectorOpen(false)}
                    onSelect={handleProductSelect}
                />
            )}
        </div>
    );
};

export default QuotationFormModal;
